import { Fragment, useEffect, useState } from "react"
import { useSnackbar, useQuery } from "@1f/react-sdk";
//Vapor Components
import Modal from "@vapor/react-material/Modal";
import Box from "@vapor/react-material/Box";
import Stack from "@vapor/react-material/Stack";
import Divider from "@vapor/react-material/Divider";
import Button from "@vapor/react-material/Button";
import{
    Save as SaveIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import IconButton from "@vapor/react-material/IconButton";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import Link from "@vapor/react-material/Link";

//Style
import { modalStyle, modalStyleUltraUltraLarge } from "../businessLogic/styles";
import { LogApi } from "../businessLogic/api/logApi";
import {BaseTableApi} from "../businessLogic/api/baseTableApi";
import { query_releaseNotes } from "../businessLogic/query/releaseNotesQueries";
import { query_base_all } from "../businessLogic/query/baseTablesQueries";
import UserApi from "../businessLogic/api/userApi";
import AnagraficaHelpers from "../businessLogic/helpers/anagraficaHelpers";

// Sub-components
import { 
    LoadingSkeleton, 
    DuplicatesListView, 
    AnagraficaDetailsView 
} from "./duplicates";

export const ModalFixDuplicate  = ({show,duplicatesAnags,tenantId,isAuthorized,userOwnerName,blocking = false,onOk,onClose}) => {
    
    const [groupedDuplicates,setGroupedDuplicates] = useState([]);
    const [loading,setLoading] = useState(isAuthorized);
    const [confirming,setConfirming] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedDuplicates, setSelectedDuplicates] = useState({});
    const [baseTipoIdentList,setBaseTipoIdentList] = useState([]);
    const [baseTipoDocList,setBaseTipoDocList] = useState([]);
    const [baseProvinciaList,setBaseProvinciaList] = useState([]);
    const [baseComuneList,setBaseComuneList] = useState([]);
    const [allUsers,setUsers] = useState([]);
    const [differentFieldsPerGroup, setDifferentFieldsPerGroup] = useState({});
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [latestRelease, setLatestRelease] = useState(null);
    const [fascicoliByAnagraficaId, setFascicoliByAnagraficaId] = useState({});
    const { enqueueSnackbar } = useSnackbar();
    const logApi = LogApi();
    const baseTableApi = BaseTableApi();
    const userApi = UserApi();
    const anagraficaHelpers = AnagraficaHelpers();
    
    const modalStyleToUse = isAuthorized ? modalStyleUltraUltraLarge : modalStyle;
    const apiBaseAll = useQuery(
        query_base_all,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiGetReleaseNotes = useQuery(
        query_releaseNotes,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );

    useEffect(()=>{
        // Se l'utente non ha i permessi, non caricare nulla
        if (!isAuthorized) {
            setLoading(false);
            return;
        }
        
        // Evita di ricaricare se i dati sono già stati caricati
        if (!show || !duplicatesAnags || duplicatesAnags.length === 0 || isDataLoaded ) {
            return;
        }

        const loadDatas = async () => {
            try {
                setLoading(true);
                
                // Raggruppa le anagrafiche duplicate per CF e P.IVA
                const grouped = duplicatesAnags.reduce((acc, item) => {
                    const anagrafica = item.anagrafica;
                    const key = `${anagrafica.pIva}-${anagrafica.cf?.toUpperCase()}`;
                    if (!acc[key]) {
                        acc[key] = [];
                    }
                    // Aggiungi l'intero item (con anagrafica, fascicoli e aziende)
                    acc[key].push(item);
                    return acc;
                }, {});
                
                // Converti l'oggetto in array di gruppi
                const groupedArray = Object.keys(grouped).map(key => ({
                    key: key,
                    duplicates: grouped[key].map(item => item.anagrafica), // Solo anagrafiche per compatibilità
                    items: grouped[key] // Items completi con fascicoli e aziende
                }));
                
                setGroupedDuplicates(groupedArray);
                
                // Inizializza le selezioni (nessuna selezione iniziale)
                const initialSelections = {};
                groupedArray.forEach((group, index) => {
                    initialSelections[index] = null;
                });
                setSelectedDuplicates(initialSelections);
                setCurrentIndex(0);
                
                // Calcola i campi diversi per ogni gruppo
                const differentFields = {};
                groupedArray.forEach((group, index) => {
                    differentFields[index] = anagraficaHelpers.FindDifferentFields(group.duplicates);
                });
                setDifferentFieldsPerGroup(differentFields);
                
                await loadBaseTables();
                await loadUsers();
                await loadLatestRelease();
                loadFascicoli(groupedArray);
                
                setIsDataLoaded(true);
            } catch (error) {
                enqueueSnackbar("Impossibile caricare le anagrafiche duplicate", { variant: "error" });
                logApi.AddLog(error,"","LoadDatas","ModalFixDuplicate");
            } finally {
                setLoading(false);
            }
        };

        const loadLatestRelease = async () => {
            try {
                const releaseNotes = await apiGetReleaseNotes.fetch();
                if (releaseNotes?.data?.data?.releasenotes?.length > 0) {
                    // Ordina per data e prendi l'ultima
                    const sorted = [...releaseNotes.data.data.releasenotes].sort(
                        (a, b) => new Date(b.Date) - new Date(a.Date)
                    );
                    setLatestRelease(sorted[0]);
                }
            } catch (e) {
                console.error("Error loading release notes:", e);
            }
        };

        const loadBaseTables = async () => {
            //Carica tabelle base se necessario
            let tbBaseAllResponse = null;
            try {
                tbBaseAllResponse = await apiBaseAll.fetch({ tenantId });

                // Verifica se la risposta contiene un errore interno
                if (!tbBaseAllResponse || tbBaseAllResponse.error) {
                    throw new Error("Errore interno API: " + tbBaseAllResponse.error);
                }

            } catch (error) {
                const errorMsg = error?.message || JSON.stringify(error);
                logApi.AddLog(errorMsg,error.stack,"loadBaseTables","ModalFixDuplicate");
                throw new Error("Impossibile recuperare le tabelle di base: " + errorMsg);
            }

            try {    
                /*base_tipo_identificazione*/
                const arrtipoIdenItems = baseTableApi.GetBaseTipoIdent(tbBaseAllResponse)
                setBaseTipoIdentList(arrtipoIdenItems);
                /*base_tipo_documento*/
                const arrTipoDocItems = baseTableApi.GetBaseTipoDocumento(tbBaseAllResponse)
                setBaseTipoDocList(arrTipoDocItems);           
                // /*base_provincia*/
                const arrProvItems = baseTableApi.GetBaseProvincia(tbBaseAllResponse)
                setBaseProvinciaList(arrProvItems);  
                const arrComuniItems = baseTableApi.GetBaseComune(tbBaseAllResponse)
                setBaseComuneList(arrComuniItems);                      
            } catch (parseError) {
                logApi.AddLog(parseError.message, parseError.stack, "loadBaseTables", "ModalFixDuplicate");
                throw new Error("Errore nel parsing delle tabelle di base: " + parseError.message);
            }
        }

        const loadUsers = async () => {
            try {
                let { allUsers, allUserTenant } = await userApi.GetAllUsersByTenantId(tenantId);
                setUsers(allUsers);
            } catch (error) {
                logApi.AddLog(error.message, error.stack, "loadUsers", "ModalFixDuplicate");
                throw new Error("Impossibile caricare gli utenti: " + error.message);
            }
        }
        
        const loadFascicoli = (groupedArray) => {
            try {
                // Organizzare i dati in una mappa anagraficaId -> fascicoli con info azienda
                const fascicoliMap = {};
                
                groupedArray.forEach(group => {
                    // Per ogni item nel gruppo (che contiene anagrafica, fascicoli e aziende)
                    group.items.forEach(item => {
                        const anagraficaId = item.anagrafica.id;
                        
                        // Inizializza l'array se non esiste
                        if (!fascicoliMap[anagraficaId]) {
                            fascicoliMap[anagraficaId] = [];
                        }
                        
                        // Set per tenere traccia dei fascicoli già inseriti (per ID)
                        const fascicoliIdsGiaInseriti = new Set(
                            fascicoliMap[anagraficaId].map(f => f.id)
                        );
                        
                        // Crea array di fascicoli con informazioni complete
                        item.fascicoli.forEach((fascicolo, index) => {
                            // Controlla se il fascicolo è già stato inserito
                            if (!fascicoliIdsGiaInseriti.has(fascicolo.id)) {
                                // Prendi l'azienda corrispondente (assumendo che l'ordine sia lo stesso)
                                const azienda = item.aziende[index] || item.aziende[0] || {};
                                
                                fascicoliMap[anagraficaId].push({
                                    id: fascicolo.id,
                                    nomeFascicolo: fascicolo.name || 'N/A',
                                    aziendaId: azienda.id || 'N/A',
                                    nomeAzienda: azienda.denominazione || 'N/A'
                                });
                                
                                // Aggiungi l'ID al set
                                fascicoliIdsGiaInseriti.add(fascicolo.id);
                            }
                        });
                    });
                });
                
                setFascicoliByAnagraficaId(fascicoliMap);
            } catch (error) {
                logApi.AddLog(error.message, error.stack, "loadFascicoli", "ModalFixDuplicate");
                // Non lanciare errore per non bloccare il caricamento della modale
                console.error("Impossibile caricare i fascicoli:", error.message);
            }
        }
        
        loadDatas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, duplicatesAnags.length]);
    
    // Reset isDataLoaded quando la modale viene chiusa
    useEffect(() => {
        if (!show) {
            setIsDataLoaded(false);
            // Resetta il loading solo se l'utente ha i permessi
            if (isAuthorized) {
                setLoading(true);
            }
        }
    }, [show, isAuthorized]);
    
    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };
    
    const handleNext = () => {
        if (currentIndex < groupedDuplicates.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };
    
    const handleSelectDuplicate = (groupIndex, duplicateIndex) => {
        console.log('Selezione:', { groupIndex, duplicateIndex });
        setSelectedDuplicates(prev => {
            const newSelection = {
                ...prev,
                [groupIndex]: duplicateIndex
            };
            console.log('Nuovo stato selezioni:', newSelection);
            return newSelection;
        });
    };        

    const handleSavePartial = async () => {
        setConfirming(true);
        try {
            // Raccogli solo le selezioni fatte fino ad ora
            const idsToDelete = [];
            
            groupedDuplicates.forEach((group, groupIndex) => {
                const selectedIndex = selectedDuplicates[groupIndex];
                
                // Solo se c'è una selezione per questo gruppo
                if (selectedIndex !== null && selectedIndex !== undefined) {
                    group.duplicates.forEach((duplicate, idx) => {
                        if (idx !== selectedIndex) {
                            idsToDelete.push(duplicate.id);
                        }
                    });
                }
            });
            
            if (idsToDelete.length === 0) {
                enqueueSnackbar("Nessuna selezione da salvare", { variant: "warning" });
                setConfirming(false);
                return;
            }
            
            console.log('IDs da eliminare (salvataggio parziale):', idsToDelete);
            
            // Chiama il callback onOk con gli ID da eliminare
            await onOk({
                idsToDelete: idsToDelete,
                selections: selectedDuplicates,
                groupedDuplicates: groupedDuplicates,
                isPartial: true
            });
            
            // Reset dello stato dopo il successo ma NON svuotiamo duplicatesAnags
            setIsDataLoaded(false);
            setGroupedDuplicates([]);
            setSelectedDuplicates({});
            setCurrentIndex(0);
        } catch (error) {
            enqueueSnackbar("Errore durante il salvataggio", { variant: "error" });
            logApi.AddLog(error.message, error.stack, "handleSavePartial", "ModalFixDuplicate");
        } finally {
            setConfirming(false);
        }
    };

    const handleClose = () => {
        // Reset dello stato ma NON svuotiamo duplicatesAnags
        setIsDataLoaded(false);
        setGroupedDuplicates([]);
        setSelectedDuplicates({});
        setCurrentIndex(0);
        // Chiama onClose invece di onOk(null)
        if (onClose) {
            onClose();
        }
    };
    
    const renderAnagraficaDetails = (anagrafica, fascicoli) => {
        return (
            <AnagraficaDetailsView
                anagrafica={anagrafica}
                differentFields={differentFieldsPerGroup[currentIndex]}
                baseTipoIdentList={baseTipoIdentList}
                baseTipoDocList={baseTipoDocList}
                baseProvinciaList={baseProvinciaList}
                baseComuniList={baseComuneList}
                allUsers={allUsers}
                fascicoli={fascicoli}
            />
        );
    };

    const handleModalOk = (e) => {
        onOk(e);
    }   
    
    return (
        <Fragment>
        <Modal
            key="modal-fix-duplicate"
            aria-labelledby="modal-fix-duplicate-title"
            aria-describedby="modal-fix-duplicate-description"
            open={show}
            disableEscapeKeyDown={blocking}
            onOk={handleModalOk}>
                <Box sx={modalStyleToUse}>
                    <Stack
                        direction="row"
                        spacing={0}
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ p: 2, pl: 3, pr: 2, flexShrink: 0 }}
                    >
                        <ExtendedTypography
                            variant="titleSmall"
                            color="primary.main"
                        >
                            {isAuthorized
                                ? 'Risolvi Duplicati - Seleziona l\'anagrafica da mantenere'
                                : 'Permessi Insufficienti'
                            }
                        </ExtendedTypography>
                        <Stack direction="row" spacing={1}>
                            {isAuthorized && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSavePartial}
                                    disabled={confirming || Object.values(selectedDuplicates).filter(v => v !== null && v !== undefined).length === 0}
                                >
                                    Conferma selezioni
                                </Button>
                            )}
                            {!blocking && (
                                <IconButton
                                    onClick={handleClose}
                                    size="small"
                                    disabled={confirming}
                                >
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                    <Divider
                        orientation="horizontal"
                        flexItem
                        sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {loading || confirming ? (
                            <LoadingSkeleton />
                        ) : (
                            isAuthorized ? (
                                <Stack direction="row" spacing={2} sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
                                    {/* Pannello sinistro con messaggi */}
                                    <Stack direction="column" spacing={2} sx={{ width: '300px', flexShrink: 0 }}>
                                        {/* Box rossa con messaggio di avviso */}
                                        <Box 
                                            sx={{ 
                                                backgroundColor: '#D82829', 
                                                p: 2, 
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'flex-start'
                                            }}
                                        >
                                            <ExtendedTypography variant="subtitle1" sx={{ color: 'white' }}>
                                                <span style={{fontWeight: 'bold'}}>
                                                    ⚠️ Questa procedura rileva la presenza di anagrafiche duplicate con lo stesso Codice Fiscale e/o Partita IVA all'interno del tenant.
                                                </span>
                                                {'\n\n'}
                                                È necessario selezionare quale anagrafica mantenere tra quelle rilevate come duplicate. 
                                                {'\n\n'}
                                                Le anagrafiche non selezionate verranno eliminate in modo definitivo.
                                            </ExtendedTypography>
                                        </Box>
                                        
                                        {/* Box con link al manuale */}
                                        {latestRelease && latestRelease.Pdf && (
                                            <Box 
                                                sx={{ 
                                                    backgroundColor: '#f5f5f5', 
                                                    p: 2, 
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd'
                                                }}
                                            >
                                                <ExtendedTypography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                    📖 Documentazione
                                                </ExtendedTypography>
                                                <Link
                                                    href={`/releaseNotes/${latestRelease.Pdf}`}
                                                    download={latestRelease.Pdf}
                                                    underline="hover"
                                                    sx={{ cursor: "pointer", fontSize: '0.875rem' }}
                                                >
                                                    📥 Scarica il manuale di versione {latestRelease.Version}
                                                </Link>
                                            </Box>
                                        )}
                                    </Stack>
                                    {/* Pannello destro con lista duplicati */}
                                    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {groupedDuplicates[currentIndex] && (
                                            <DuplicatesListView
                                                currentGroup={groupedDuplicates[currentIndex]}
                                                currentIndex={currentIndex}
                                                totalGroups={groupedDuplicates.length}
                                                selectedIndex={selectedDuplicates[currentIndex]}
                                                onSelectDuplicate={(idx) => handleSelectDuplicate(currentIndex, idx)}
                                                onPrevious={handlePrevious}
                                                onNext={handleNext}
                                                renderDetails={renderAnagraficaDetails}
                                                fascicoliByAnagraficaId={fascicoliByAnagraficaId}
                                            />
                                        )}
                                    </Box>
                                </Stack>
                            ) : (                                    
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <ExtendedTypography variant="h6" color="error">
                                        La funzione di risoluzione dei duplicati è irreversibile e richiede l'intervento di {userOwnerName} che è l' OWNER del tenant.
                                    </ExtendedTypography>
                                </Box>                                
                            )
                        )}
                    </Box>
                </Box>                    
        </Modal>
    </Fragment>
    )
}